import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Pin, PinOff, Trash2 } from "lucide-react";

export default function CandidateNotes({ candidateId, currentUserId, isOwner }) {
    const [items, setItems] = useState([]);
    const [body, setBody] = useState("");
    const [pinned, setPinned] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/candidates/${candidateId}/notes`);
            // pinned first, then newest
            data.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || b.created_at.localeCompare(a.created_at));
            setItems(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [candidateId]);

    const add = async (e) => {
        e.preventDefault();
        if (!body.trim()) return;
        try {
            await api.post(`/candidates/${candidateId}/notes`, { body: body.trim(), pinned });
            setBody("");
            setPinned(false);
            toast.success("Note added");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const togglePin = async (n) => {
        try {
            await api.patch(`/candidates/${candidateId}/notes/${n.id}`, { pinned: !n.pinned });
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (n) => {
        if (!window.confirm("Delete this note?")) return;
        try {
            await api.delete(`/candidates/${candidateId}/notes/${n.id}`);
            toast.success("Deleted");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="space-y-5">
            <form onSubmit={add} className="border border-border bg-white p-5 space-y-3">
                <Textarea required rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a note about this candidate..." className="rounded-none" data-testid="note-body" />
                <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={pinned} onCheckedChange={(v) => setPinned(Boolean(v))} className="rounded-none" data-testid="note-pin" />
                        <span>Pin to top</span>
                    </label>
                    <Button type="submit" className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-9" data-testid="note-submit">Add note</Button>
                </div>
            </form>

            {items.length === 0 ? (
                <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No notes yet.</div>
            ) : (
                <div className="space-y-3">
                    {items.map((n) => (
                        <div key={n.id} className={`border bg-white p-4 ${n.pinned ? "border-ikb/40 bg-blue-50/30" : "border-border"}`} data-testid={`note-row-${n.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <div className="text-xs text-muted-foreground font-mono mb-1.5">
                                        <span className="text-foreground font-medium">{n.author?.name || "Unknown"}</span>
                                        {n.author?.role && <> · {n.author.role}</>}
                                        <> · {new Date(n.created_at).toLocaleString()}</>
                                    </div>
                                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{n.body}</p>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => togglePin(n)} className="text-muted-foreground hover:text-ikb" data-testid={`pin-note-${n.id}`}>
                                        {n.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                                    </button>
                                    {(n.author_id === currentUserId || isOwner) && (
                                        <button onClick={() => remove(n)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-note-${n.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
